/**
 * SidebarFooterNav — the account block pinned to the bottom of the sidebar:
 * workspace settings, linked accounts, help, and the user profile menu.
 *
 * Extracted from AppSidebar so secondary surfaces that replace the platform
 * sidebar (Teammate Code) get the same footer instead of losing account access
 * entirely. Owns the linked-accounts dialog because every caller needs it and
 * nothing outside this block opens it.
 */

import { useState } from "react"
import { HelpCircle, Plug, Settings } from "lucide-react"

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { usePermissions } from "@/hooks/use-permissions"
import { useSettingsDialogsStore } from "@/store/slices/settings-dialogs-slice"
import { LinkedAccountsDialog } from "@/components/me/linked-accounts-dialog"
import { SidebarUserProfile } from "./sidebar-user-profile"

interface SidebarFooterNavProps {
  /** Icon-rail mode — the profile block drops to an avatar-only trigger. */
  collapsed?: boolean
  /**
   * Hides the settings/linked-accounts/help menu, leaving only the profile.
   * CoWork mode uses this to strip the app nav from the footer.
   */
  navHidden?: boolean
}

export function SidebarFooterNav({ collapsed = false, navHidden = false }: SidebarFooterNavProps) {
  const { isAdmin } = usePermissions()
  const workspaceSettingsOpen = useSettingsDialogsStore((s) => s.workspaceSettings.open)
  const openWorkspaceSettings = useSettingsDialogsStore((s) => s.openWorkspaceSettings)
  const [linkedAccountsOpen, setLinkedAccountsOpen] = useState(false)

  return (
    <>
      {!navHidden && (
        <SidebarMenu>
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={workspaceSettingsOpen}
                onClick={() => openWorkspaceSettings()}
              >
                <Settings className="size-4" />
                <span>Settings</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={linkedAccountsOpen}
              onClick={() => setLinkedAccountsOpen(true)}
            >
              <Plug className="size-4" />
              <span>Linked Accounts</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <a
                href="https://store-docs.no-downtime.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <HelpCircle className="size-4" />
                <span>Help</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      )}
      <div className="px-2 mt-2 group-data-[collapsible=icon]:px-0">
        <SidebarUserProfile collapsed={collapsed} />
      </div>
      <LinkedAccountsDialog open={linkedAccountsOpen} onOpenChange={setLinkedAccountsOpen} />
    </>
  )
}

SidebarFooterNav.displayName = "SidebarFooterNav"
